/**
 * analytics.js — Conversation-history + analytics tracking (old/new parity).
 *
 * Mirrors every tracking call the old Fogarty widget fires:
 *
 *   saveAnalytics flags (old script.js)
 *     entry_point_click_counter         line 730   — first widget open
 *     clicked_on_banner                 line 736   — SHOP NOW banner click
 *     longitude/latitude/user_agent     line 1100  — getClientDetails on init
 *     time_spent                        line 2457  — calcTime on unload
 *     cta_link / cta_text               line 1368  — any <a> click in chatbot
 *     waiting_period_threshold_crossed  line 1580  — 7s without bot reply
 *     clicked_on_text_live_person       line 4169  — RedChirp form shown
 *     redchirp_form_submitted           line 4296  — RedChirp form submitted
 *
 *   track_conversation_history (POST conversation HTML — old fires at ~17 sites)
 *     We collapse those 17 sites into a single dirty-flag debounce that fires
 *     after any UI-state-changing event: messages, cart settle, checkout steps,
 *     contact-form submit, and unload (via fetch keepalive).
 *
 *   update_analytics (update_guided_flows)
 *     Old fires only for "Contact our team" (line 2560). We mirror that
 *     narrowly — no broad guided-flow tracking.
 *
 * All calls are fire-and-forget — errors log via console.warn and never break
 * the chat UI. Session creation is reused from chat-handler.ensureSession().
 */

import { on, getState } from "../../core/state.js";
import { ensureSession } from "../base/chat-handler.js";
import * as api from "../../core/api.js";

let _initialized = false;
let _sessionInitFired = false;
let _sessionOpenTs = 0;
let _historyTimer = null;
let _historyPendingUntilSettled = false;
let _ctaDelegationAttached = false;
let _ctaRecentHrefs = new Map(); // href -> last-fire ts
let _waitingTimer = null;

const HISTORY_DEBOUNCE_MS = 600;
const CTA_DEDUP_MS = 500;
const WAITING_THRESHOLD_MS = 7000;

const warn = (label, err) => console.warn(`[RedConcierge analytics] ${label}`, err);

// Resolve geolocation with a hard timeout. Never rejects — denied / unavailable
// / timeout all resolve to {}. Parity with old getClientDetails.
function getGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      resolve(v);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          finish({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => finish({}),
        { timeout: 5000, enableHighAccuracy: false, maximumAge: 600000 }
      );
    } catch {
      finish({});
    }
    setTimeout(() => finish({}), 5500);
  });
}

// Shadow root + .ws-messages accessor. Used for both the HTML snapshot and
// the delegated cta-link click listener.
function shadowRoot() {
  return document.getElementById("redconcierge-widget")?.shadowRoot ?? null;
}
function messagesEl() {
  return shadowRoot()?.querySelector(".ws-messages") ?? null;
}

// Snapshot the rendered .ws-messages HTML for the conversation-history POST.
// Strips transient nodes (typing indicator, cart overlays) so the saved
// history reflects the settled conversation — parity with old trackHistory
// which removed .loading-animation before sending.
function snapshotConversationHTML() {
  const messages = messagesEl();
  if (!messages) return "";
  const clone = messages.cloneNode(true);
  clone
    .querySelectorAll(".ws-typing-indicator, .ws-cart-overlay, [class*='ws-loading']")
    .forEach((el) => el.remove());
  return clone.innerHTML;
}

function currentSessionId() {
  return getState().session?.id ?? null;
}
function currentTableNo() {
  return getState().session?.tableNo ?? "";
}

function saveHistorySnapshot() {
  const sessionId = currentSessionId();
  if (!sessionId) return;
  const html = snapshotConversationHTML();
  if (!html) return;
  api
    .saveConversationHistory({
      sessionId,
      conversationHistory: html,
      tableNo: currentTableNo(),
    })
    .catch((err) => warn("conversation-history save failed", err));
}

// ─── conversation-history (dirty-flag debounce) ────────────────
// Old fires trackHistory at ~17 sites; new fires once per debounce window.
function markHistoryDirty() {
  if (getState().chat?.isStreaming) {
    _historyPendingUntilSettled = true;
    return;
  }
  if (_historyTimer) clearTimeout(_historyTimer);
  _historyTimer = setTimeout(() => {
    _historyTimer = null;
    if (getState().chat?.isStreaming) {
      _historyPendingUntilSettled = true;
      return;
    }
    saveHistorySnapshot();
  }, HISTORY_DEBOUNCE_MS);
}

function saveHistoryCheckpoint() {
  if (_historyTimer) {
    clearTimeout(_historyTimer);
    _historyTimer = null;
  }
  _historyPendingUntilSettled = true;
  saveHistorySnapshot();
}

function flushHistoryWhenSettled() {
  if (!_historyPendingUntilSettled) return;
  _historyPendingUntilSettled = false;
  markHistoryDirty();
}

// ─── init analytics (geo + user_agent + entry_point_click_counter) ──
async function fireSessionOpenAnalytics() {
  try {
    const sessionId = await ensureSession();
    if (!sessionId) return;
    _sessionOpenTs = Date.now();
    const geo = await getGeo();
    await api.saveAnalytics({
      session_id: sessionId,
      user_agent: navigator.userAgent,
      latitude: geo.latitude,
      longitude: geo.longitude,
      entry_point_click_counter: 1,
    });
  } catch (err) {
    warn("session-open analytics failed", err);
  }
}

// ─── simple fire-and-forget save-analytics helpers ────────────
function fireFlag(payload, label) {
  const sessionId = currentSessionId();
  if (!sessionId) return;
  api
    .saveAnalytics({ session_id: sessionId, ...payload })
    .catch((err) => warn(`${label} analytics failed`, err));
}

// ─── cta-link delegated click listener ────────────────────────
// Old fires { cta_link, cta_text } on any <a> click inside the chatbot
// container. We attach one delegated listener on the shadow root and walk up
// from the click target. Dedup repeated fires of the same href within 500 ms.
function attachCtaDelegation() {
  if (_ctaDelegationAttached) return;
  const root = shadowRoot();
  if (!root) return;
  root.addEventListener(
    "click",
    (e) => {
      const a = e.target?.closest?.("a[href]");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href) return;
      const now = Date.now();
      const last = _ctaRecentHrefs.get(href) ?? 0;
      if (now - last < CTA_DEDUP_MS) return;
      _ctaRecentHrefs.set(href, now);
      fireFlag(
        { cta_link: href, cta_text: (a.textContent || "").trim() },
        "cta-link"
      );
    },
    true
  );
  _ctaDelegationAttached = true;
}

// ─── unload (time_spent + final history snapshot) ─────────────
function fireUnload() {
  if (!_sessionOpenTs) return;
  const sessionId = currentSessionId();
  if (!sessionId) return;
  const timeSpent = Math.round((Date.now() - _sessionOpenTs) / 1000);
  try {
    api.saveAnalyticsBeacon({ session_id: sessionId, time_spent: timeSpent });
  } catch (err) {
    warn("unload analytics failed", err);
  }
  // Old also POSTs track_conversation_history in its beforeunload path
  // (script.js 3782). Mirror that with a keepalive history beacon so the
  // final settled DOM is captured server-side.
  try {
    const html = snapshotConversationHTML();
    if (html) {
      api.saveConversationHistoryBeacon({
        sessionId,
        conversationHistory: html,
        tableNo: currentTableNo(),
      });
    }
  } catch (err) {
    warn("unload history failed", err);
  }
}

export function init() {
  if (_initialized) return;
  _initialized = true;

  // 1. First panel open — request geolocation + log session start.
  on("ws-toggle", ({ isOpen }) => {
    if (!isOpen || _sessionInitFired) return;
    _sessionInitFired = true;
    fireSessionOpenAnalytics();
    // Attach cta-link delegation now that the shadow root exists and the
    // user is interacting with the widget.
    attachCtaDelegation();
  });

  // 2. Conversation-history POST — dirty-flag debounce triggered by any
  // UI-state-changing event. Old fires at ~17 sites; we collapse them into
  // one coalesced POST per settle window.
  on("ws-message-added", () => markHistoryDirty());
  on("ws-message-updated", () => markHistoryDirty());
  on("ws-chat-history-checkpoint", () => saveHistoryCheckpoint());
  on("ws-chat-settled", () => flushHistoryWhenSettled());
  on("ws-cart-busy", ({ active } = {}) => {
    if (active === false) markHistoryDirty();
  });
  on("ws-checkout", () => markHistoryDirty());
  on("ws-checkout-method", () => markHistoryDirty());
  on("ws-checkout-submit", () => markHistoryDirty());
  on("ws-checkout-edit", () => markHistoryDirty());

  // 3. Banner SHOP NOW click — parity with old shopNowBanner (line 736).
  on("ws-banner-cta-click", () => fireFlag({ clicked_on_banner: true }, "banner-click"));

  // 4. Guided-flow click — old fires update_analytics ONLY for "Contact our
  // team" (script.js 2560). Match that narrowly; ignore other guided buttons.
  on("ws-prompt-select", ({ value }) => {
    if (value !== "__contact__") return;
    const sessionId = currentSessionId();
    if (!sessionId) return;
    api
      .updateAnalytics({
        sessionId,
        updateGuidedFlows: ["Contact our team"],
      })
      .catch((err) => warn("update-analytics failed", err));
  });

  // 5. RedChirp "Text a Live Person" form shown (old line 4169).
  on("ws-contact-text", () => fireFlag({ clicked_on_text_live_person: true }, "live-person-shown"));

  // 6. RedChirp form submitted (old line 4296) — also flags the history dirty
  // so the conversation snapshot captures the submitted state.
  on("ws-contact-submit", () => {
    fireFlag({ redchirp_form_submitted: true }, "redchirp-submitted");
    markHistoryDirty();
  });

  // 7. Waiting-period threshold — 7 s without a bot reply (old line 1580).
  // Keyed off ws-chat-typing: timer starts when typing goes active, cleared
  // when typing ends (first token or completion / error).
  on("ws-chat-typing", ({ active } = {}) => {
    if (_waitingTimer) {
      clearTimeout(_waitingTimer);
      _waitingTimer = null;
    }
    if (active) {
      _waitingTimer = setTimeout(() => {
        _waitingTimer = null;
        fireFlag({ waiting_period_threshold_crossed: true }, "waiting-threshold");
      }, WAITING_THRESHOLD_MS);
    }
  });

  // 8. Tab/window close — time_spent + final history snapshot via keepalive.
  window.addEventListener("beforeunload", fireUnload);
}
